<template>
    <template v-if="safeOptions.length === 0">
        <div class="relative w-full cursor-not-allowed rounded-xl py-2.5 pl-3 pr-10 rtl:pl-10 rtl:pr-3 text-start text-sm bg-[var(--theme-input)] border border-[var(--theme-border)] text-[var(--theme-text-tertiary)] opacity-75">
            {{ displayPlaceholder }}
        </div>
    </template>
    <!-- Use primitive (valueAttribute) for v-model so Headless UI never receives option objects (avoids internal .some on null) -->
    <Listbox v-else as="div" v-model="selectedValue" class="t-rich-user-select">
        <div class="relative" ref="containerRef">
            <ListboxButton 
                class="t-rich-user-select__button relative w-full cursor-pointer rounded-xl py-2.5 pl-3 pr-10 rtl:pl-10 rtl:pr-3 text-start text-sm transition-colors bg-[var(--theme-input)] border border-[var(--theme-border)] text-[var(--theme-text)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]/25 focus:border-[var(--theme-primary)]"
                :class="{ '!border-[var(--theme-error)] !focus:ring-[var(--theme-error)]/25 !focus:border-[var(--theme-error)]': hasError }"
            >
                <slot v-if="$slots.label" name="label" :option="selectedOption" />
                <span v-else class="block truncate" :class="selectedOption ? 'text-[var(--theme-text)]' : 'text-[var(--theme-text-tertiary)]'">
                    {{ selectedOption ? selectedOption[textAttribute] : displayPlaceholder }}
                </span>

                <!-- Clear button -->
                <span 
                    v-if="clearable && selectedValue != null" 
                    @click.stop="clearSelection" 
                    class="absolute inset-y-0 right-8 flex items-center group"
                >
                    <svg class="h-4 w-4 text-[var(--theme-text-tertiary)] group-hover:text-[var(--theme-text)] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </span>

                <!-- Chevron down -->
                <span class="pointer-events-none absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 flex items-center pr-3 rtl:pr-0 rtl:pl-3">
                    <svg class="h-5 w-5 text-[var(--theme-text-tertiary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </ListboxButton>

            <transition 
                leave-active-class="transition duration-100 ease-in" 
                leave-from-class="opacity-100" 
                leave-to-class="opacity-0"
                @after-leave="searchQuery = ''; dropdownPositionStyle = ''"
            >
                <ListboxOptions 
                    ref="dropdownRef"
                    class="t-rich-user-select__dropdown absolute z-[100] mt-1.5 w-full rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)] text-[var(--theme-text)] text-sm shadow-lg focus:outline-none flex flex-col min-h-0"
                    :class="dropdownClass || (searchable ? 'max-h-96' : 'max-h-80')"
                    :style="dropdownPositionStyle"
                >
                    <!-- Search Input -->
                    <div v-if="searchable" class="sticky top-0 flex-shrink-0 bg-[var(--theme-card)] p-2 border-b border-[var(--theme-border)]">
                        <div class="relative">
                            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--theme-text-tertiary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input 
                                ref="searchInput"
                                v-model="searchQuery"
                                type="text"
                                :placeholder="displaySearchPlaceholder"
                                class="w-full pl-9 pr-3 rtl:pl-3 rtl:pr-9 py-2 text-sm rounded-lg bg-[var(--theme-surface)] border border-[var(--theme-border)] text-[var(--theme-text)] placeholder-[var(--theme-text-tertiary)] focus:outline-none focus:border-[var(--theme-primary)]"
                                @click.stop
                                @keydown.stop
                            />
                        </div>
                    </div>

                    <!-- Options Container: always has max-height + overflow-auto so list scrolls -->
                    <div class="overflow-y-auto overflow-x-hidden py-1.5 min-h-0 flex-1" :class="searchable ? 'max-h-80' : 'max-h-72'">
                        <!-- Empty State -->
                        <div v-if="filteredOptions.length === 0" class="px-3 py-4 text-[var(--theme-text-secondary)] text-center">
                            <svg class="w-8 h-8 mx-auto mb-2 text-[var(--theme-text-tertiary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {{ searchQuery ? t('common.components.no_results_found') : t('common.components.no_options_available') }}
                        </div>

                        <!-- Options: pass only primitive so Headless UI match() never sees option objects -->
                        <ListboxOption 
                            v-for="(option, index) in filteredOptions" 
                            :key="option != null && option[valueAttribute] !== undefined ? option[valueAttribute] : index" 
                            :value="getOptionValue(option)" 
                            as="template" 
                            v-slot="{ active, selected }"
                        >
                            <li 
                                class="relative cursor-pointer select-none py-2.5 px-4 transition-colors"
                                :class="[
                                    active ? 'bg-[var(--theme-surface)]' : '',
                                    selected ? 'bg-[var(--theme-surface)]' : ''
                                ]"
                            >
                                <slot name="option" :option="option" :selected="selected" :active="active">
                                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate text-[var(--theme-text)]']">
                                        {{ option && option[textAttribute] }}
                                    </span>
                                </slot>
                                <!-- Checkmark (only when selected) -->
                                <span 
                                    v-if="selected" 
                                    class="absolute inset-y-0 end-[2.5%] flex items-center pl-3 rtl:pl-0 rtl:pr-3 text-[var(--theme-primary)]"
                                >
                                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </span>
                            </li>
                        </ListboxOption>
                    </div>
                </ListboxOptions>
            </transition>
        </div>
    </Listbox>
</template>

<script setup>
import { ref, watch, computed, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';

const { t } = useI18n();

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: '', // when empty, use translation key common.components.select_an_option
    },
    valueAttribute: {
        type: String,
        default: 'id',
    },
    textAttribute: {
        type: String,
        default: 'name',
    },
    clearable: {
        type: Boolean,
        default: false,
    },
    hasError: {
        type: Boolean,
        default: false,
    },
    dropdownClass: {
        type: String,
        default: '',
    },
    searchable: {
        type: Boolean,
        default: false,
    },
    searchPlaceholder: {
        type: String,
        default: '', // when empty, use translation key common.components.search_placeholder
    },
    searchKeys: {
        type: Array,
        default: () => ['name', 'symbol'],
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

const searchQuery = ref('');
const searchInput = ref(null);
const containerRef = ref(null);
const dropdownRef = ref(null);
const dropdownPositionStyle = ref('');

const recomputePosition = async () => {
    await nextTick();
    
    const parentEl = containerRef.value;
    const dropdownEl = dropdownRef.value?.$el ?? dropdownRef.value;
    
    if (!parentEl || !dropdownEl) return;
    
    const parentRect = parentEl.getBoundingClientRect();
    const dropdownWidth = dropdownEl.offsetWidth;
    const winWidth = window.innerWidth;
    const dir = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
    
    let newStyle = '';
    
    if (dir === 'ltr') {
        const expectedRight = parentRect.left + dropdownWidth;
        if (expectedRight > winWidth - 10) {
            newStyle = 'left: auto !important; right: 0 !important;';
        } else {
            newStyle = 'left: 0 !important; right: auto !important;';
        }
    } else {
        const expectedLeft = parentRect.right - dropdownWidth;
        if (expectedLeft < 10) {
            newStyle = 'right: auto !important; left: 0 !important;';
        } else {
            newStyle = 'right: 0 !important; left: auto !important;';
        }
    }
    
    dropdownPositionStyle.value = newStyle;
};

watch(dropdownRef, (el) => {
    if (el) {
        recomputePosition();
        window.addEventListener('resize', recomputePosition);
    } else {
        window.removeEventListener('resize', recomputePosition);
        dropdownPositionStyle.value = '';
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', recomputePosition);
});

const safeOptions = computed(() => {
    const arr = Array.isArray(props.options) ? props.options : [];
    return arr.filter(item => item != null);
});

const safeSearchKeys = computed(() => {
    const keys = props.searchKeys;
    return Array.isArray(keys) && keys.length > 0 ? keys : ['name', 'symbol'];
});

/** Placeholder and search placeholder: use translation when prop is empty (default). */
const displayPlaceholder = computed(() =>
    props.placeholder !== '' ? props.placeholder : t('common.components.select_an_option')
);
const displaySearchPlaceholder = computed(() =>
    props.searchPlaceholder !== '' ? props.searchPlaceholder : t('common.components.search_placeholder')
);

/** Normalize modelValue: only number/string primitive or null. Never pass array or empty string (Headless UI can call .some on wrong type). */
const normalizedModelValue = computed(() => {
    const v = props.modelValue;
    if (v == null) return null;
    if (Array.isArray(v)) return v.length > 0 ? v[0] : null;
    if (typeof v === 'string' && v === '') return null;
    if (typeof v === 'object') return null;
    return v;
});

const filteredOptions = computed(() => {
    if (!searchQuery.value.trim()) return safeOptions.value;
    const query = String(searchQuery.value).toLowerCase().trim();
    const keys = safeSearchKeys.value;
    if (!keys || keys.length === 0) return safeOptions.value;
    return safeOptions.value.filter(option => {
        if (option == null) return false;
        return keys.some(key => {
            const value = option[key];
            return value != null && String(value).toLowerCase().includes(query);
        });
    });
});

/** Listbox v-model: primitive only (id/symbol etc.) so Headless UI never does .some on option objects */
const selectedValue = ref(normalizedModelValue.value);

/** Resolve full option for display/slots */
const selectedOption = computed(() => {
    const v = selectedValue.value;
    if (v == null) return null;
    return safeOptions.value.find(opt => opt != null && opt[props.valueAttribute] === v) ?? null;
});

/** Return only primitive (number/string) for ListboxOption so Headless UI never sees object or calls .some on non-array */
function getOptionValue(option) {
    if (option == null) return undefined;
    const v = option[props.valueAttribute];
    if (v === undefined || v === null) return undefined;
    if (typeof v === 'object' || typeof v === 'function') return undefined;
    return v;
}

/** Sync selectedValue from props */
function syncFromProps() {
    const v = normalizedModelValue.value;
    selectedValue.value = v;
}
syncFromProps();

watch(selectedValue, (newVal) => {
    emit('update:modelValue', newVal);
    emit('change', newVal);
});

watch(() => [props.modelValue, safeOptions.value], () => {
    syncFromProps();
}, { deep: true });

const clearSelection = () => {
    selectedValue.value = null;
};
</script>

<style scoped>
/* Theme-based select: single place for all colors; uses CSS variables (light/dark). */
.t-rich-user-select :deep(button) {
    background-color: var(--theme-input);
    border: 1px solid var(--theme-border);
    color: var(--theme-text);
}
.t-rich-user-select :deep(button:hover),
.t-rich-user-select :deep(button[data-headlessui-state="open"]) {
    background-color: var(--theme-input);
    border-color: var(--theme-primary);
}
.t-rich-user-select :deep(.t-rich-user-select__dropdown) {
    background-color: var(--theme-card);
    border: 1px solid var(--theme-border);
    color: var(--theme-text);
}
.t-rich-user-select :deep(.t-rich-user-select__dropdown input) {
    background-color: var(--theme-surface);
    border-color: var(--theme-border);
    color: var(--theme-text);
}
.t-rich-user-select :deep(.t-rich-user-select__dropdown input::placeholder) {
    color: var(--theme-text-tertiary);
}
.t-rich-user-select :deep(.t-rich-user-select__dropdown li) {
    color: var(--theme-text);
}
.t-rich-user-select :deep(.t-rich-user-select__dropdown li:hover),
.t-rich-user-select :deep(.t-rich-user-select__dropdown li[data-headlessui-state="active"]),
.t-rich-user-select :deep(.t-rich-user-select__dropdown li[data-headlessui-state="selected"]) {
    background-color: var(--theme-surface);
}
</style>
